import { useTranslation } from 'react-i18next';

import { AudioPlayer } from '@/components/ui/AudioPlayer';
import type { MockAudioQueue } from '@/hooks/useMockAudioQueue';

/**
 * Записи Listening в моке: на компьютере — как на экзамене (слово владельца 21.10.2026):
 * каждая часть звучит ОДИН раз, без перемотки и скорости; дослушанную до конца заново не включить.
 * Пауза есть — Pause / Play с того же места (правка владельца 21.10.2026, после демо);
 * общий таймер мока при этом идёт. Части не ждут друг друга (правка владельца 21.10.2026):
 * любую можно включить в любой момент; одновременно звучит одна — включённая ставит
 * звучащую на паузу (playingNow в AudioPlayer), та потом продолжается с места. Доигравшая часть
 * сама включает следующую, только если ту ещё не трогали. С 27.10.2026 (сборка 1.1.2, слово владельца «всё как
 * на сайте») так же и на узком экране — прежде там был обычный плеер с перемоткой и скоростью. В тренажёрах —
 * обычный плеер.
 *
 * ⚠️ ПЕРВУЮ ЧАСТЬ ВКЛЮЧАЕТ УЧЕНИК. Звук без нажатия браузер не даст; после первого нажатия
 * страница считается «тронутой», и следующие части Chrome включает сам. Если браузер всё же
 * откажет — плеер встанет на паузу, и ученик нажмёт Play сам.
 *
 * ⚠️ ПРОСЛУШАННОЕ — В ПОПЫТКЕ (техдолг 432, 23.10.2026): место и «дослушано» каждой части
 * хранит useMockAudioQueue в section_states. После F5 дослушанная закрыта, начатая продолжает
 * с сохранённого места (назад не больше чем на 5 с); после возврата с вкладки Reading — с того
 * же места: плеер снимается со страницы, и раньше начатая часть начиналась с нуля. Сама после F5
 * часть не заиграет — звук без нажатия браузер не даст, ученик нажмёт Play.
 */

type Props = {
  src: string;
  /** Номер записи в секции, с нуля. */
  index: number;
  count: number;
  queue: MockAudioQueue;
  /** Режим экзамена (мок — всегда, с 27.10.2026 и на телефоне). Без него — обычный плеер. */
  exam: boolean;
};

export function MockListeningAudio({ src, index, count, queue, exam }: Props) {
  const { t } = useTranslation();
  if (!exam) return <AudioPlayer src={src} preload="none" />;
  const played = queue.done.has(index);
  return (
    <div className="space-y-1" data-mock-audio={played ? 'played' : 'open'}>
      <AudioPlayer
        src={src}
        preload="none"
        examOnce
        startNonce={queue.nonces[index]}
        startAt={queue.startAt(index)}
        closed={played}
        onPlaying={() => queue.playing(index)}
        onTime={(sec) => queue.time(index, sec)}
        onPaused={queue.paused}
        onEnded={() => queue.ended(index, count)}
      />
      <p className="text-xs text-on-surface-variant">
        {played ? t('ieltsMock.audioOnce.played') : t('ieltsMock.audioOnce.note')}
      </p>
    </div>
  );
}
